import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCreateWorkspace } from '@/api/workspaces/useWorkspace';
import { Button } from '@/component-library/primitives/Button/Button';
import { Textarea } from '@/component-library/primitives/Textarea/Textarea';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { Input } from '@/component-library/widgets/Input/Input';
import OptionSelector from '@/component-library/widgets/OptionSelector/OptionSelector';
import { CreateWorkspaceDetails } from '@/models/WorkspaceDetails';
import { dashboardBottomActionBarClassName } from '@/modules/Dashboard/dashboardBottomActionBarClassName';
import queryKeys from '@/modules/shared/query/queryKeys';
import { AppRoutes } from '@/utils/routes/router';

const CreateWorkspace = () => {
  const createWorkspaceMutation = useCreateWorkspace();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });
  const queryClient = useQueryClient();

  useEffect(() => {
    let timerBeforeNavigation: NodeJS.Timeout;
    if (toastInfo.toastType === 'success') {
      timerBeforeNavigation = setTimeout(() => {
        queryClient.invalidateQueries([queryKeys.workspaces]);
        navigate(AppRoutes.workspaces);
      }, 2000);
    }
    return () => {
      clearTimeout(timerBeforeNavigation);
    };
  }, [navigate, queryClient, toastInfo]);

  const schema = useMemo(() => {
    return yup
      .object()
      .shape({
        name: yup.string().required(t('CommonErrors.nameRequired')),
      })
      .required();
  }, [t]);

  const [settings, setSettings] = useState(t('CreateWorkspacePage.sendMessagesNormallyOption'));
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateWorkspaceDetails>({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: CreateWorkspaceDetails) => {
    const sendData = {
      name: data.name,
      settings: settings,
      identifier: 'id, email',
      teamMembers: 1,
      createdBy: '',
      description: data.description,
    };

    createWorkspaceMutation.mutate(sendData as CreateWorkspaceDetails, {
      onSuccess: () => {
        setToastInfo({ message: t('CreateWorkspacePage.toastSuccess'), toastType: 'success' });
        setOpen(true);
      },
      onError: () => {
        setToastInfo({ message: t('CreateWorkspacePage.toastError'), toastType: 'error' });
        setOpen(true);
      },
    });
  };

  const handleSettingsSelect = (option: string) => {
    setSettings(option);
  };

  return (
    <>
      <div className="flex h-full flex-col pb-28">
        <div className="mx-auto w-full max-w-3xl">
          <h1 className="text-xl font-semibold text-custom-black">{t('CreateWorkspacePage.title')}</h1>
          <p className="mt-1 text-sm text-custom-darkSilver">{t('CreateWorkspacePage.subtitle')}</p>

          <form className="mt-8" onSubmit={handleSubmit(onSubmit)}>
            <section className="space-y-4 rounded-xl border border-custom-lightSilver bg-white p-6 shadow-[2px_2px_4px_rgba(0,0,0,0.04)]">
              <Input
                label={t('CreateWorkspacePage.nameLabel')}
                placeholder={t('CreateWorkspacePage.namePlaceholder')}
                {...register('name')}
                error={errors.name?.message as string}
                data-cy="workspaceName"
              />
              <Textarea
                {...register('description')}
                placeholder={`${t('CreateWorkspacePage.descriptionPlaceholder')}...`}
                label={t('CreateWorkspacePage.descriptionLabel')}
                optional={true}
              />
            </section>

            <h2
              id="create-workspace-delivery-heading"
              className="mb-4 mt-10 text-xs font-semibold uppercase tracking-[0.12em] text-custom-darkSilver"
            >
              {t('CreateWorkspacePage.howToDeliverMessagesLabel')}
            </h2>
            <div
              role="radiogroup"
              aria-labelledby="create-workspace-delivery-heading"
              className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5"
            >
              <OptionSelector
                variant="light"
                title={t('CreateWorkspacePage.sendMessagesNormallyOption')}
                content={t('CreateWorkspacePage.sendMessagesNormallyDescription')}
                gradientOption="1"
                isSelected={settings === t('CreateWorkspacePage.sendMessagesNormallyOption')}
                onSelect={handleSettingsSelect}
              />
              <OptionSelector
                variant="light"
                title={t('CreateWorkspacePage.testEmailDeliveryOption')}
                content={t('CreateWorkspacePage.testEmailDeliveryDescription')}
                gradientOption="2"
                isSelected={settings === t('CreateWorkspacePage.testEmailDeliveryOption')}
                onSelect={handleSettingsSelect}
              />
              <OptionSelector
                variant="light"
                title={t('CreateWorkspacePage.neverSendMessagesOption')}
                content={t('CreateWorkspacePage.neverSendMessagesDescription')}
                gradientOption="3"
                isSelected={settings === t('CreateWorkspacePage.neverSendMessagesOption')}
                onSelect={handleSettingsSelect}
              />
            </div>

            <div className={dashboardBottomActionBarClassName}>
              <div className="flex w-full max-w-3xl items-center justify-between px-4 sm:px-0">
                <Button variant="cancel" type="button" onClick={() => navigate(AppRoutes.workspaces)}>
                  {t('CreateWorkspacePage.linkCancel')}
                </Button>
                <Button
                  type="submit"
                  className="px-4 !w-52"
                  data-cy="createWorkspaceButton"
                  isLoading={createWorkspaceMutation.isLoading || createWorkspaceMutation.isSuccess}
                >
                  {t('CreateWorkspacePage.buttonCreateWorkspace')}
                </Button>
              </div>
            </div>
          </form>
        </div>
        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={toastInfo.toastType as ToastType}
          />
        </ProvideToasts>
      </div>
    </>
  );
};

export default CreateWorkspace;
